import React, { useState, useMemo } from 'react';
import { Organization, UserProfile } from '../types';
import { Avatar } from './common';

interface TransferOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  org: Organization;
  currentUser: UserProfile;
  allUsers: UserProfile[];
  onTransferOwnership: (params: {
    orgId: string;
    newAdminEmail: string;
    newAdminId?: string;
    newAdminName?: string;
    outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
    reason?: string;
  }) => Promise<void>;
  t?: (key: string, reps?: any) => string;
}

export const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
  isOpen,
  onClose,
  org,
  currentUser,
  allUsers,
  onTransferOwnership
}) => {
  const [targetType, setTargetType] = useState<'existing_staff' | 'email'>('existing_staff');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [customEmail, setCustomEmail] = useState<string>('');
  const [customName, setCustomName] = useState<string>('');
  const [outgoingAdminRole, setOutgoingAdminRole] = useState<'vice_principal' | 'teacher' | 'stepped_down'>('vice_principal');
  const [reason, setReason] = useState<string>('');
  const [confirmationInput, setConfirmationInput] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter available school staff (Vice Principals, Teachers) excluding the current principal
  const eligibleStaff = useMemo(() => {
    return allUsers.filter(u => 
      u.id !== currentUser.id &&
      u.id !== org.adminId &&
      (u.orgId === org.id || (org.vicePrincipalEmails || []).includes(u.email || ''))
    ).sort((a, b) => {
      // Prioritize Vice Principals first
      const aIsVp = a.isVicePrincipal || (org.vicePrincipalEmails || []).includes(a.email || '');
      const bIsVp = b.isVicePrincipal || (org.vicePrincipalEmails || []).includes(b.email || '');
      if (aIsVp && !bIsVp) return -1;
      if (!aIsVp && bIsVp) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [allUsers, currentUser.id, org]);

  if (!isOpen) return null;

  const isConfirmationMatched = confirmationInput.trim().toLowerCase() === org.name.trim().toLowerCase();

  const selectedStaffUser = selectedStaffId ? allUsers.find(u => u.id === selectedStaffId) : null;

  const resolvedTargetEmail = targetType === 'existing_staff' 
    ? (selectedStaffUser?.email || '').trim().toLowerCase()
    : customEmail.trim().toLowerCase();

  const resolvedTargetName = targetType === 'existing_staff'
    ? selectedStaffUser?.name || 'School Successor'
    : customName.trim() || 'New School Principal';

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!resolvedTargetEmail || !validateEmail(resolvedTargetEmail)) {
      setError('Please provide a valid Google account email address for the successor.');
      return;
    }

    if (resolvedTargetEmail === (currentUser.email || '').toLowerCase().trim()) {
      setError('You cannot transfer institutional ownership to your own current email.');
      return;
    }

    if (!isConfirmationMatched) {
      setError(`Safety safeguard: Please type the institution name "${org.name}" exactly to confirm.`);
      return;
    }

    setIsProcessing(true);
    try {
      await onTransferOwnership({
        orgId: org.id,
        newAdminEmail: resolvedTargetEmail,
        newAdminId: targetType === 'existing_staff' ? selectedStaffUser?.id : undefined,
        newAdminName: resolvedTargetName,
        outgoingAdminRole,
        reason: reason.trim() || undefined
      });
      onClose();
    } catch (err: any) {
      console.error("Transfer ownership failed:", err);
      setError(err?.message || 'Failed to complete ownership transfer. Please verify your connection.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-xl w-full shadow-2xl border border-slate-100 max-h-[92vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center flex-shrink-0">
              <span className="material-icons-round text-2xl">swap_horiz</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 leading-tight">Transfer Primary Ownership</h2>
              <p className="text-xs text-slate-500 mt-0.5">{org.name} • Institutional Handover</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors disabled:opacity-50"
          >
            <span className="material-icons-round text-lg">close</span>
          </button>
        </div>

        {/* Warning Banner */}
        <div className="mt-4 p-3.5 bg-amber-50/80 border border-amber-200 rounded-2xl flex items-start gap-3">
          <span className="material-icons-round text-amber-700 text-xl flex-shrink-0 mt-0.5">warning</span>
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong className="font-bold block mb-0.5">Permanent Authority Transfer</strong>
            You are transferring the Primary Principal &amp; Owner role of <strong>{org.name}</strong>. The successor will become the permanent root administrator with full governance over the institution.
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium flex items-center gap-2">
            <span className="material-icons-round text-sm">error</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          
          {/* Step 1: Successor Selection */}
          <div className="space-y-3">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              Step 1: Select New Principal &amp; Owner
            </label>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setTargetType('existing_staff'); setError(null); }}
                className={`py-2 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                  targetType === 'existing_staff'
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="material-icons-round text-sm">group</span>
                <span>Existing Staff ({eligibleStaff.length})</span>
              </button>

              <button
                type="button"
                onClick={() => { setTargetType('email'); setError(null); }}
                className={`py-2 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                  targetType === 'email'
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="material-icons-round text-sm">mail</span>
                <span>Enter Google Email</span>
              </button>
            </div>

            {targetType === 'existing_staff' ? (
              <div>
                {eligibleStaff.length === 0 ? (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-500 text-xs">
                    <p className="font-medium">No other verified staff or Vice Principals found in this school yet.</p>
                    <button
                      type="button"
                      onClick={() => setTargetType('email')}
                      className="mt-2 text-indigo-600 font-bold underline"
                    >
                      Enter successor's Google Email directly
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {eligibleStaff.map((staff) => {
                      const isVp = staff.isVicePrincipal || (org.vicePrincipalEmails || []).includes(staff.email || '');
                      const isSelected = selectedStaffId === staff.id;
                      return (
                        <div
                          key={staff.id}
                          onClick={() => setSelectedStaffId(staff.id)}
                          className={`p-3 rounded-xl border cursor-pointer flex items-center justify-between transition-all ${
                            isSelected
                              ? 'bg-indigo-50/70 border-indigo-400 ring-2 ring-indigo-200 shadow-xs'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Avatar user={staff} className="w-9 h-9" />
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-bold text-slate-800 text-xs">{staff.name}</span>
                                {isVp && (
                                  <span className="px-1.5 py-0.5 bg-teal-100 text-teal-800 text-[10px] font-bold rounded-md">
                                    Vice Principal
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-slate-500 font-mono block">{staff.email}</span>
                            </div>
                          </div>
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300'}`}>
                            {isSelected && <span className="material-icons-round text-xs">check</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Successor Google / Gmail Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="incoming.principal@gmail.com"
                    required={targetType === 'email'}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    The successor will be designated as the Primary Principal upon authentication.
                  </p>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Successor Full Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="e.g. Dr. Rajesh Sharma"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Post-Transfer Role for Outgoing Principal */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              Step 2: Your Role After Handover
            </label>
            <p className="text-[11px] text-slate-500">
              Decide what administrative capacity you wish to maintain in {org.name}:
            </p>

            <div className="space-y-2">
              <label
                className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-all ${
                  outgoingAdminRole === 'vice_principal'
                    ? 'bg-teal-50/70 border-teal-300 ring-2 ring-teal-200 shadow-2xs'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="outgoingRole"
                  value="vice_principal"
                  checked={outgoingAdminRole === 'vice_principal'}
                  onChange={() => setOutgoingAdminRole('vice_principal')}
                  className="mt-0.5 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900">Remain as Vice Principal (Co-Admin)</span>
                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-md">
                      Recommended
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                    You keep day-to-day administrative authority (classes, broadcasts, student verification, calendar) alongside the new Principal.
                  </p>
                </div>
              </label>

              <label
                className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-all ${
                  outgoingAdminRole === 'teacher'
                    ? 'bg-indigo-50/70 border-indigo-300 ring-2 ring-indigo-200 shadow-2xs'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="outgoingRole"
                  value="teacher"
                  checked={outgoingAdminRole === 'teacher'}
                  onChange={() => setOutgoingAdminRole('teacher')}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900">Transition to Teacher</span>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                    Step down from school-wide administration and continue as an instructional teacher for your assigned classrooms.
                  </p>
                </div>
              </label>

              <label
                className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-all ${
                  outgoingAdminRole === 'stepped_down'
                    ? 'bg-amber-50/70 border-amber-300 ring-2 ring-amber-200 shadow-2xs'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="outgoingRole"
                  value="stepped_down"
                  checked={outgoingAdminRole === 'stepped_down'}
                  onChange={() => setOutgoingAdminRole('stepped_down')}
                  className="mt-0.5 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900">Step Down Completely</span>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                    Remove your account from this institution's staff roster (e.g. following retirement or transfer to a different organization).
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Step 3: Handover Reason / Memo */}
          <div className="space-y-1.5 pt-2 border-t border-slate-100">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              Step 3: Handover Reason / Audit Memo (Optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Annual leadership rotation / handover to incoming Principal"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden"
            />
          </div>

          {/* Step 4: Safety Safeguard Confirmation */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              Step 4: Confirm by Typing Institution Name
            </label>
            <p className="text-[11px] text-slate-500">
              To prevent accidental transfer, please type <strong className="text-slate-800 font-bold select-all">"{org.name}"</strong> below:
            </p>
            <input
              type="text"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder={org.name}
              required
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-hidden"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !resolvedTargetEmail || !isConfirmationMatched}
              className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isProcessing ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Transferring Ownership...</span>
                </>
              ) : (
                <>
                  <span className="material-icons-round text-sm">verified_user</span>
                  <span>Transfer Primary Ownership</span>
                </>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};
